/**
 * Single source of truth for environment-derived config.
 * All components read from `config`; never from `import.meta.env` directly.
 *
 * Env resolution order (see docker/40-flysafe-config.sh):
 *   1. window.__CONFIG__  — runtime config.js injected by the container at
 *      startup. Lets one built image run any tenant/env without a rebuild.
 *   2. import.meta.env    — build-time Vite env, used by `pnpm dev` + tests.
 */

import { RELEASED, VERSION } from './version';

declare global {
  interface Window {
    __CONFIG__?: Record<string, string | undefined>;
  }
}

/** Runtime value first (`/config.js`), then build-time fallback. */
function readEnv(key: string): string | undefined {
  const runtime = typeof window !== 'undefined' ? window.__CONFIG__ : undefined;
  const v = runtime?.[key];
  if (v !== undefined && v !== '') return v;
  return import.meta.env[key];
}

function bool(v: string | undefined, fallback = false): boolean {
  if (v === undefined || v === '') return fallback;
  if (v === 'true' || v === '1') return true;
  if (v === 'false' || v === '0') return false;
  return fallback;
}

function parseLngLat(v: string | undefined, fallback: [number, number]): [number, number] {
  if (!v) return fallback;
  const parts = v.split(',').map((s) => Number(s.trim()));
  if (parts.length !== 2 || parts.some((n) => Number.isNaN(n))) return fallback;
  return [parts[0]!, parts[1]!];
}

export type DataSource = 'mock' | 'live';
export type MqttSchema = 'legacy' | 'output' | 'both';
export const DATA_SOURCE_STORAGE_KEY = 'flysafe.dataSource';

// Data source is a UI-only choice, NOT an env var. The ProfileMenu toggle is
// the single source of truth — it writes localStorage and reloads, so every
// bootstrap consumer (MSW, auth, MQTT factory) re-reads from a clean slate.
// This default only applies to a first-ever load before the user has chosen.
const DEFAULT_DATA_SOURCE: DataSource = 'live';

function resolveDataSource(): DataSource {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(DATA_SOURCE_STORAGE_KEY);
    if (stored === 'mock' || stored === 'live') return stored;
  }
  return DEFAULT_DATA_SOURCE;
}

const dataSource = resolveDataSource();

export const config = {
  tenant: {
    id: readEnv('VITE_TENANT_ID') ?? 'mock',
    label: readEnv('VITE_TENANT_LABEL') ?? 'Mock',
    dotColor: readEnv('VITE_TENANT_DOT_COLOR') ?? '#6b7280',
  },
  dataSource,
  mqtt: {
    url: readEnv('VITE_MQTT_URL') ?? '',
    topicPrefix: readEnv('VITE_TOPIC_PREFIX') ?? 'flysafe/rtm',
    username: readEnv('VITE_MQTT_USERNAME') ?? '',
    password: readEnv('VITE_MQTT_PASSWORD') ?? '',
    /**
     * Which topic schema this tenant's broker speaks (ticket 86e2jbe3d).
     *
     *   'legacy' — flat v0 topics (`networkid/#`, `flightplan/+`, …)
     *   'output' — category-first v1 (`v1/output/aircraft/{sn}/…`)
     *   'both'   — subscribe to both; correct during a parallel-publish
     *              window, wasteful outside one
     *
     * Sandbox was hard-cut to 'output' on 2026-07-30 — the legacy topics
     * return zero messages there, so pointing a 'legacy' build at it
     * renders an empty fleet. Other tenants are still 'legacy' until the
     * backend cuts them over, hence the per-env switch rather than a
     * code change.
     */
    schema: (() => {
      const v = readEnv('VITE_MQTT_SCHEMA');
      return v === 'output' || v === 'both' || v === 'legacy' ? v : 'legacy';
    })() as MqttSchema,
  },
  api: {
    base: readEnv('VITE_API_BASE') ?? '',
  },
  // Aircraft Custodian API (inventory drone config + live state). The browser
  // hits the same-origin `/custodian-api` proxy (vite dev / worker), which
  // forwards to CUSTODIAN_UPSTREAM and injects X-Tenant-Id from CUSTODIAN_TENANT
  // server-side — no client-side env vars, mirroring the /grafana pattern.
  custodian: {
    base: '/custodian-api',
  },
  // RTM Services observability (ClickUp 86e1w8ude). The browser calls the
  // SAME-ORIGIN `/grafana/*` proxy (worker.ts in deployed envs, the Vite
  // `/grafana` route in dev), which strips the prefix, forwards to Grafana,
  // and injects the service-account Bearer token server-side — so the token
  // never reaches the client bundle and there is no CORS issue. Mirrors the
  // existing `/partnerships` + `/api/aircraft` proxy pattern.
  // `token` is an optional direct-mode fallback only (normally empty).
  grafana: {
    base: readEnv('VITE_GRAFANA_BASE') ?? '/grafana',
    token: readEnv('VITE_GRAFANA_TOKEN') ?? '',
    promUid: readEnv('VITE_GRAFANA_PROM_UID') ?? 'prometheus',
    lokiUid: readEnv('VITE_GRAFANA_LOKI_UID') ?? '',
    // Kubernetes namespaces treated as "RTM services" — drives the services
    // table + hosting roll-up. Per-tenant filtering = narrow this list to the
    // tenant's namespace(s). Comma-separated → OR-regex at query time.
    rtmNamespaces: (
      readEnv('VITE_GRAFANA_RTM_NAMESPACES') ??
      'shared-dss,shared-kafka,shared-emqx,shared-influx,shared-flink-operator,shared-keycloak,shared-otel,shared-tempo,shared-logging,shared-iart-tools-blender'
    )
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    pollMs: Number(readEnv('VITE_GRAFANA_POLL_MS') ?? 15_000),
  },
  // RTM OI search API (ClickUp 86e1w8ude). Client hits the SAME-ORIGIN
  // `/oi-api` proxy (worker.ts / Vite dev), which injects the tenant token
  // server-side — the token is tenant-scoped and never ships to the browser.
  oiApi: {
    base: readEnv('VITE_OI_API_BASE') ?? '/oi-api',
    // ENG-313 — Blender lists declarations from several parties; ours carry
    // `originating_party` = the tenant id ("airmarket" on airmarket).
    ownParty: readEnv('VITE_OI_OWN_PARTY') ?? readEnv('VITE_TENANT_ID') ?? 'mock',
  },
  // Cross-tenant Partnership Controller (separate service from the main API).
  // Default to same-origin ('') so calls go through the Vite dev proxy /
  // worker.ts `/partnerships` route — the controller sends no CORS headers,
  // so the browser cannot call it cross-origin directly. Set
  // VITE_PARTNERSHIPS_BASE to a full URL only once the controller enables CORS.
  // In mock mode MSW intercepts `*/partnerships/*`, so the host is irrelevant.
  partnerships: {
    base: readEnv('VITE_PARTNERSHIPS_BASE') ?? '',
  },
  oidc: {
    authority: readEnv('VITE_OIDC_AUTHORITY') ?? '',
    clientId: readEnv('VITE_OIDC_CLIENT_ID') ?? '',
  },
  map: {
    mapboxToken: readEnv('VITE_MAPBOX_TOKEN') ?? '',
    defaultStyle: readEnv('VITE_DEFAULT_MAP_STYLE') ?? 'mapbox://styles/mapbox/light-v11',
    // lat,lng in env → [lat, lng] convention (Mapbox uses [lng, lat] so we flip at call sites)
    defaultCenter: parseLngLat(readEnv('VITE_DEFAULT_MAP_CENTER'), [53.5461, -113.4938]),
    defaultZoom: Number(readEnv('VITE_DEFAULT_MAP_ZOOM') ?? 10),
  },
  features: {
    aiChat: bool(readEnv('VITE_ENABLE_AI_CHAT')),
    aiLayerSearch: bool(readEnv('VITE_ENABLE_AI_LAYER_SEARCH')),
    weatherTab: bool(readEnv('VITE_ENABLE_WEATHER_TAB')),
    mqttPublish: bool(readEnv('VITE_ENABLE_MQTT_PUBLISH')),
    // Real Keycloak login (ticket 86e232ykg). Off by default so existing
    // dev/demo flows keep using the mock role picker in LoginModal
    // untouched — this is additive, not a replacement. Flip on once
    // config.oidc.authority/clientId point at a real realm+client.
    keycloakAuth: bool(readEnv('VITE_ENABLE_KEYCLOAK_AUTH')),
  },
  // Aircraft data plane — Path A (REST poll + MQTT bridge) ships both
  // sources behind toggles. When BE widens `live_ads` (Path B) we set
  // `adsbExchange.enabled = false` and only MQTT remains.
  aircraft: {
    adsbExchange: {
      enabled: bool(readEnv('VITE_AIRCRAFT_ADSBX_ENABLED'), true),
      // URL of the proxy fronting ADSB Exchange. v1 runs its own proxy
      // at `/api/aircraft/`; v2 dev points at v1's sandbox proxy so we
      // don't need our own. Override per environment.
      proxyUrl: readEnv('VITE_ADSB_PROXY_URL') ?? 'https://sandbox.stage.flysafe.live/api/aircraft',
      apiKey: readEnv('VITE_ADSB_KEY') ?? '',
      distanceKm: Number(readEnv('VITE_ADSB_DIST_KM') ?? 250),
      pollIntervalMs: Number(readEnv('VITE_ADSB_POLL_MS') ?? 1000),
    },
    mqttBridge: {
      enabled: bool(readEnv('VITE_AIRCRAFT_MQTT_ENABLED'), true),
    },
    /** Eviction watchdog — drop aircraft that nobody refreshes for this
     *  long. Matches v1's `STALE_MS = 120_000`. */
    staleMs: 120_000,
  },
  telemetry: {
    // Sentry DSN is a PUBLIC value (safe in the client bundle by design). The
    // hardcoded fallback means FE error tracking works in every env without
    // extra config; override per-env via VITE_SENTRY_DSN (or config.js) to
    // point a tenant at its own Sentry project. `||` so an empty env value
    // (VITE_SENTRY_DSN=) falls back rather than disabling Sentry.
    sentryDsn:
      readEnv('VITE_SENTRY_DSN') ||
      'https://a0a90477592a99f1722b7ff8a677e2ed@o4511704857706496.ingest.us.sentry.io/4511704864391168',
    otelCollector: readEnv('VITE_OTEL_COLLECTOR') ?? '',
    // Grafana Faro — FE error logs into our self-hosted Loki. The SDK POSTs
    // directly to the receiver `url` with the `apiKey` sent as the `x-api-key`
    // header. Empty `url` disables Faro. Per-env via config.js / .env.<tenant>.
    faro: {
      url: readEnv('VITE_FARO_URL') ?? '',
      apiKey: readEnv('VITE_FARO_API_KEY') ?? '',
    },
  },
  // App version stamped on Faro/Sentry events (MetaApp.version) and shown in
  // the UI. Deliberately NOT read via readEnv() — a runtime/env-sourced version
  // could disagree with the bundle actually running (moving image tags,
  // per-pod config.js). The compiled-in constant is the only value that can't
  // lie. Bump it in src/lib/version.ts. See that file for the rules.
  appVersion: VERSION,
  releasedAt: RELEASED,
  // Minimum log level that reaches the console. Empty = verbose ('debug') in
  // dev, 'warn' in prod. Set VITE_LOG_LEVEL=debug on a deployed env (it flows
  // through config.js with no rebuild) to surface full MQTT-connection
  // diagnostics in the browser console when troubleshooting (ClickUp 86e1yvx21).
  logLevel: readEnv('VITE_LOG_LEVEL') ?? '',
  isDev: import.meta.env.DEV,
  isMock: dataSource === 'mock',
};

/** Persist the chosen data source and reload so every bootstrap reads it
 *  fresh. Reload-on-switch avoids stale MSW handlers / auth state / MQTT
 *  store residue (see ProfileMenu data-source toggle). */
export function setDataSource(next: DataSource): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(DATA_SOURCE_STORAGE_KEY, next);
  location.reload();
}

export type Config = typeof config;
